import { useCallback, useEffect, useMemo } from "react";
import { DefinedPropertyObject, GenericEntity, ICustomFieldSearch, StringIndexedDict } from "../../../api/GenericTypes";
import { UseTabActions } from "../../tables/Tabs/TableTabsTypes";
import {
  Control,
  DeepPartial,
  DefaultValues,
  FieldErrors,
  Path,
  PathValue,
  useForm,
  UseFormGetValues,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { CustomFieldFilterLayoutForm } from "../../../Customization/CustomFieldFilters/CustomFieldFilterLayout/CustomFieldFilterLayout";
import {
  CustomFieldSearchQueryForm,
  ICustomFieldSearchForm,
} from "../../../Customization/CustomFieldFilters/CustomFieldFilterBuilder/CustomFieldFilterBuilder";
import { useUnpaginate } from "../../../api/BaseEntityApi";
import { CustomField, CustomFieldFilters, customFieldConstants } from "../../../api/CustomFields/CustomFields";
import {
  consolidateCustomFieldFilters,
  convertCustomFieldSearchQueryFormToCustomFieldSearchQuery,
  convertICustomFieldSearch,
  customFieldFiltersSchema,
  isValueCustomFieldSearchOperator,
  // yupCustomFieldFiltersValidationResolver,
} from "../../../Customization/CustomFieldFilters/CustomFieldFilterBuilder/CustomFieldFilterUtils";
import styles from "./TabStoreFilterFormProvider.module.css";
import { LoadingWrapper } from "../../LoadingWrapper";
import { CustomTypeEntityType } from "../../../api/CustomTypes/CustomTypesBase";
// TabStore FilterSideBar custom hooks
// Common interface to be used for Entity Tables Sidebar Filter component

export interface TabStoreFilterSidebarProps<
  Entity extends GenericEntity,
  FormInterface extends StringIndexedDict,
  Filters extends StringIndexedDict
> {
  initialValues?: FormInterface;
  onFormChange?: (formValues: FormInterface) => void; // Optional callback to retrieve form values
  dispatchTabStore?: (action: UseTabActions<Entity, Filters, FormInterface>) => Promise<void>; // Dispatcher for TabsStore
  tabsLoading?: boolean; // TabsStore loading state
  currentTab?: string; // TabsStore currentTab
  hideReset?: boolean; // Hide reset button
  setFilters?: React.Dispatch<React.SetStateAction<Filters>>; // Optional stateSetter
}

interface TabStoreFilterFormProviderChildProps<FormInterface extends StringIndexedDict & CustomFieldFilterLayoutForm> {
  register: UseFormRegister<FormInterface>;
  control: Control<FormInterface, any, FormInterface>;
  errors: FieldErrors<FormInterface>;
  reset: UseFormReset<FormInterface>;
  watch: UseFormWatch<FormInterface>;
  getValues: UseFormGetValues<FormInterface>;
  setValue: UseFormSetValue<FormInterface>;
  onReset: (e: React.MouseEvent) => Promise<void>;
  customFieldsDict?: Record<number, CustomField>; // Optional custom fields dictionary
}

interface TabStoreFilterFormProviderProps<
  Entity extends GenericEntity,
  FormInterface extends StringIndexedDict & CustomFieldFilterLayoutForm,
  EntityFilters extends StringIndexedDict
> {
  initialValues?: FormInterface;
  defaultValues: DefinedPropertyObject<FormInterface>;
  defaultFilters?: EntityFilters;
  formToFilters: (values: FormInterface) => EntityFilters;
  onFormChange?: TabStoreFilterSidebarProps<Entity, FormInterface, EntityFilters>["onFormChange"];
  dispatchTabStore?: TabStoreFilterSidebarProps<Entity, FormInterface, EntityFilters>["dispatchTabStore"];
  setFilters?: TabStoreFilterSidebarProps<Entity, FormInterface, EntityFilters>["setFilters"];
  tabsLoading?: boolean;
  customFieldFilters?: ICustomFieldSearchForm[];
  customTypeConstraint?: CustomTypeEntityType; // Optional custom type constraint for filtering
  children: (props: TabStoreFilterFormProviderChildProps<FormInterface>) => React.ReactNode;
}

export const TabStoreFilterFormProvider = <
  Entity extends GenericEntity,
  FormInterface extends StringIndexedDict & CustomFieldFilterLayoutForm,
  EntityFilters extends StringIndexedDict & ICustomFieldSearch
>({
  initialValues,
  defaultValues,
  defaultFilters,
  formToFilters,
  dispatchTabStore,
  onFormChange,
  setFilters,
  tabsLoading,
  customTypeConstraint,
  children,
}: TabStoreFilterFormProviderProps<Entity, FormInterface, EntityFilters>) => {
  const filters = useMemo(() => defaultFilters, [defaultFilters]);
  const {
    data: customFieldsDict,
    status,
    fetchStatus,
    error,
  } = useUnpaginate<CustomField, CustomFieldFilters>(
    customFieldConstants.resource,
    {
      customTypeEntityTypes: customTypeConstraint ? [customTypeConstraint] : undefined,
    },
    { enabled: !!customTypeConstraint }
  );

  return (
    <div className="flex" style={{ width: "100%", height: "100%", overflow: "hidden", position: "relative" }}>
      {tabsLoading && (
        <div className={styles.overlay}>
          <div className={styles.loader}>
            <div />
            <div />
            <div />
          </div>
        </div>
      )}
      <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error} type="filters">
        <TabStoreFilterFormProviderCore
          initialValues={initialValues}
          defaultValues={defaultValues}
          defaultFilters={filters}
          formToFilters={formToFilters}
          dispatchTabStore={dispatchTabStore}
          onFormChange={onFormChange}
          setFilters={setFilters}
          tabsLoading={tabsLoading}
          customFieldsDict={customFieldsDict}
          children={children}
        />
      </LoadingWrapper>
    </div>
  );
};

interface TabStoreFilterFormProviderCoreProps<
  Entity extends GenericEntity,
  FormInterface extends StringIndexedDict & CustomFieldFilterLayoutForm,
  EntityFilters extends StringIndexedDict
> extends TabStoreFilterFormProviderProps<Entity, FormInterface, EntityFilters> {
  customFieldsDict?: Record<number, CustomField>;
}
const TabStoreFilterFormProviderCore = <
  Entity extends GenericEntity,
  FormInterface extends StringIndexedDict & CustomFieldFilterLayoutForm,
  EntityFilters extends StringIndexedDict & ICustomFieldSearch
>({
  initialValues,
  defaultValues,
  defaultFilters,
  formToFilters,
  dispatchTabStore,
  onFormChange,
  setFilters,
  tabsLoading,
  customFieldsDict,
  children,
}: TabStoreFilterFormProviderCoreProps<Entity, FormInterface, EntityFilters>) => {
  // const customFieldFilters = useMemo(() => initialValues?.customFieldFilters, [initialValues]);
  // This function is used to pre-process the custom field filters
  const processCustomFieldFilters = useCallback(
    (values: ICustomFieldSearchForm[]) =>
      values.reduce((acc, value) => {
        acc.push({
          ...value,
          id: value.id ?? "",
          name: value.name ?? "",
          filterQuery: convertICustomFieldSearch(value.filterQuery, customFieldsDict ?? {}),
        } as ICustomFieldSearchForm);
        return acc;
      }, [] as ICustomFieldSearchForm[]),
    [customFieldsDict]
  );

  /**
   * @author @CorradoSurmanowicz
   * Memoizes and merges initialValues with defaultValues, processing customFieldFilters if present.
   */
  const values = useMemo(() => {
    const merged = { ...defaultValues, ...initialValues } as FormInterface;
    if (Array.isArray(merged.customFieldFilters) && merged.customFieldFilters.length) {
      merged.customFieldFilters = processCustomFieldFilters(merged.customFieldFilters);
    }
    return merged;
  }, [defaultValues, initialValues, processCustomFieldFilters]);

  const {
    register,
    unregister,
    control,
    getValues,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormInterface>({
    defaultValues: defaultValues as DefaultValues<FormInterface>,
    values: values,
    // resolver:
    //   Array.isArray(customFieldFilters) && !!customFieldFilters.length
    //     ? yupCustomFieldFiltersValidationResolver
    //     : undefined,
    // shouldUnregister: true,
  });

  useEffect(() => {
    if (!values.customFieldFilters || !Array.isArray(values.customFieldFilters)) {
      // console.log("Unregistering customFieldFilters");
      setValue("customFieldFilters" as Path<FormInterface>, [] as PathValue<FormInterface, Path<FormInterface>>);
    }
  }, [setValue, unregister, values.customFieldFilters]);

  // TODO this needs to run before restoring the tab state from history
  // useEffect(() => {
  //   if (!tabsLoading) {
  //     (async () =>
  //       await dispatchTabStore?.({
  //         type: "setTab",
  //         options: { keepPrevious: true },
  //         payload: {
  //           settings: {
  //             sidebarFilters: {
  //               ...(defaultValues as FormInterface),
  //             },
  //             filters: formToFilters(defaultValues),
  //           },
  //         },
  //       }))();
  //   }
  // }, [defaultValues, dispatchTabStore, formToFilters, tabsLoading]);

  // useEffect(() => {
  //   console.log("errors", errors);
  // }, [errors]);

  const consolidateCFF = useCallback(async (form: FormInterface): Promise<EntityFilters> => {
    let filters: EntityFilters = {} as EntityFilters;
    if (
      Object.hasOwn(form, "customFieldFilters") &&
      Array.isArray(form.customFieldFilters) &&
      !!form.customFieldFilters.length
    ) {
      const filteredFilters = form.customFieldFilters
        .filter((f) => f.showInSidebar)
        .filter((f) => {
          if (f.type === "flat") {
            if (!f.filterQuery?.operator) return false;
            if (!isValueCustomFieldSearchOperator(f.filterQuery.operator)) return f.active;
            if (
              isValueCustomFieldSearchOperator(f.filterQuery.operator) &&
              (!f.filterQuery.value || (Array.isArray(f.filterQuery.value) && f.filterQuery.value.length === 0))
            )
              return false;
            return true;
          } else {
            return f.active === true;
          }
        });

      let validatedFilters: CustomFieldFilterLayoutForm | undefined;
      try {
        validatedFilters = (await customFieldFiltersSchema.validate(
          { customFieldFilters: filteredFilters },
          { strict: true }
        )) as any;
      } catch (e) {
        console.error(
          "Custom field filter validation failed: ",
          e instanceof Error && Object.hasOwn(e, "message") ? e.message : e
        );
      }
      if (!validatedFilters) {
        filters.filterQuery = null;
      }
      if (validatedFilters?.customFieldFilters) {
        filters.filterQuery =
          Array.isArray(form.customFieldFilters) && !!form.customFieldFilters.length
            ? consolidateCustomFieldFilters(validatedFilters.customFieldFilters as ICustomFieldSearchForm[]).filterQuery
            : null;
      }
    }
    return filters;
  }, []);

  const formChange = useCallback(
    async (value: DeepPartial<FormInterface>) => {
      const filters = { ...formToFilters(value as FormInterface), ...(await consolidateCFF(value as FormInterface)) };
      const sidebarvalues = { ...value };
      if (Array.isArray(sidebarvalues.customFieldFilters) && !!sidebarvalues.customFieldFilters.length) {
        sidebarvalues.customFieldFilters = sidebarvalues.customFieldFilters
          .filter((f) => f)
          .map((f) => ({
            ...f,
            filterQuery: convertCustomFieldSearchQueryFormToCustomFieldSearchQuery(
              (f?.filterQuery as CustomFieldSearchQueryForm) ?? ({} as CustomFieldSearchQueryForm),
              true
            ),
          }));
      }
      onFormChange?.(value as FormInterface);
      setFilters?.((prev) => ({ ...prev, ...filters }));
      if (!tabsLoading) {
        await dispatchTabStore?.({
          type: "setTab",
          options: { keepPrevious: true },
          payload: {
            settings: {
              sidebarFilters: {
                ...(sidebarvalues as FormInterface),
              },
              filters: filters,
            },
          },
        });
      }
    },
    [consolidateCFF, dispatchTabStore, formToFilters, onFormChange, setFilters, tabsLoading]
  );

  useEffect(() => {
    const subscription = watch(async (value: DeepPartial<FormInterface>) => {
      await formChange(value);
      // const isValid = await trigger();
      // if (isValid) {
      //   const filters = { ...formToFilters(value as FormInterface), ...consolidateCFF(value as FormInterface) };
      //   onFormChange?.(value as FormInterface);
      //   setFilters?.((prev) => ({ ...prev, ...filters }));
      //   if (!tabsLoading) {
      //     await dispatchTabStore?.({
      //       type: "setTab",
      //       options: { keepPrevious: true },
      //       payload: {
      //         settings: {
      //           sidebarFilters: {
      //             ...(value as FormInterface),
      //           },
      //           filters: filters,
      //         },
      //       },
      //     });
      //   }
      // }
    });
    return () => subscription.unsubscribe();
  }, [formChange, watch]);

  const onReset = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      const _defaults = {
        ...defaultValues,
        ...(values?.customFieldFilters ? { customFieldFilters: values.customFieldFilters } : {}),
      };
      reset(_defaults);
      await dispatchTabStore?.({
        type: "setTab",
        payload: {
          settings: {
            sidebarFilters: _defaults,
            filters: defaultFilters ? { ...formToFilters(_defaults), ...defaultFilters } : formToFilters(_defaults),
          },
        },
      });
    },
    [defaultFilters, defaultValues, dispatchTabStore, formToFilters, reset, values]
  );

  return <>{children({ register, control, errors, reset, watch, getValues, setValue, onReset, customFieldsDict })}</>;
};
